import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import MatchingApi, { MatchingQueryParam } from '../MatchingPreview/api/MatchingApi';
import Matching, { MatchingType } from '../MatchingPreview/MatchingConstants';
import { matchingPreviewSelector, setComponentType, setMatchingOrderBy, setMatchingOrderDirection, setMatchingQuery, setOpenModal, setTriggerMarketingMatching, setTriggerMatching } from '../MatchingPreview/reducers/MatchingPreviewReducer';
import { ComponentType } from '../SavedSearch/SavedSearch';
import { TonnageMatchingDisplayModel } from '../Tonnage/models/TonnageMatchingDisplayModel';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import { SearchType } from '../Tonnage/reducers/TonnageSearchReducer';
import { UserGroupEnum } from '../UserManagement/UserGroupEnum';
import CargoApi, { DeleteDuplicateCargosParam } from './api/CargoApi';
import { CargoMatchingDisplayModel } from './models/CargoMatchingDisplayModel';
import CargoModel from './models/CargoModel';
import { cargoInputSelector, setSelectedCargoId } from './reducers/CargoInputReducer';
import { cargoSearchSelector, setCargoSearchResults, setCheckedCargos, setDatatableSelectedCargoIndex, setOrderBy, setOrderDirection, setSearchResultTotalRecord, setSearchType, setTriggerCargoSearch } from './reducers/CargoSearchReducer';

const CargoTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    const history = useHistory();

    let cargoData = useSelector(cargoSearchSelector);
    const cargoInputViewData = useSelector(cargoInputSelector);
    const matchingSelectorData = useSelector(matchingPreviewSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [orderDetail, setOrderDetail] = useState<string>("");
    const divRef = React.createRef<HTMLDivElement>();

    const formatDateMMMDD = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD MMM");
    };

    if (matchingSelectorData.triggerMatching === 0) {
        const urlParams = new URLSearchParams(window.location.search);

        let componentType = urlParams.get("componentType");
        if (componentType) {
            let matchingQueryString = urlParams.get("matchingQuery");
            if (matchingQueryString) {
                let matchingQuery = JSON.parse(decodeURIComponent(matchingQueryString)) as MatchingQueryParam;
                dispatch(setMatchingQuery(matchingQuery));
                dispatch(setComponentType(ComponentType.Cargo));
                dispatch(setTriggerMatching((new Date()).getTime()));
            }
        }

        let matchingType = urlParams.get("matchingType");
        let matchingModelString = urlParams.get("matchingModel");
        if (matchingModelString) {
            let matchingModel = JSON.parse(decodeURIComponent(matchingModelString)) as TonnageMatchingDisplayModel;
            let detailString = matchingType + " for {" + matchingModel.vesselName + "} {"
                + matchingModel.dwt.toLocaleString() + "} open {"
                + matchingModel.portName + "} {"
                + formatDateMMMDD(matchingModel.dateOpenStart) + " - " + formatDateMMMDD(matchingModel.dateOpenEnd) + "}";

            Utility.Alert.Info(detailString, 10000000000);
        }
    }

    useEffect(() => {
        if (matchingSelectorData.type === ComponentType.Cargo) {
            dispatch(showLoading(true));

            MatchingApi.MatchingCargo(matchingSelectorData.matchingQuery)
                .then((response: BaseResponse<CargoModel[]>) => {
                    if (response.success) {
                        dispatch(setCargoSearchResults(response.data));
                        dispatch(setSearchResultTotalRecord(response.totalRecord));
                        dispatch(setSearchType(SearchType.Matching));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                }).catch((error) => {
                    Utility.Alert.Error(error);
                })
                .finally(() => {
                    dispatch(showLoading(false));
                });
        }
    }, [matchingSelectorData.triggerMatching])

    useEffect(() => {
        if (cargoData.triggerMatchingAreaModel.triggerMatchingArea > 0) {
            matchingAreas(cargoData.triggerMatchingAreaModel.model, cargoData.triggerMatchingAreaModel.matchingType);
        }
    }, [cargoData.triggerMatchingAreaModel.triggerMatchingArea])

    const columns = [
        {
            Field: "chartererCompanyName",
            Title: "Account Name",
            AllowSort: true,
            Width: "150px",
            Render: (rowData: CargoModel) => {
                let previewButton = rowData.orderDetail && rowData.orderDetail.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Grid container>
                        <Grid item xs={11}>
                            {rowData.chartererCompanyName}
                        </Grid>
                        <Grid item xs={1}>
                            <Button
                                className="tonnage-table-preview-full-tc"
                                onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                                    if (rowData.orderDetail && rowData.orderDetail.length > 0) {
                                        setAnchorEl(event.currentTarget);
                                        setOrderDetail(rowData.orderDetail);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setAnchorEl(null)
                                }}
                                disabled={!rowData.orderDetail || rowData.orderDetail.length === 0}
                            >
                                {previewButton}
                            </Button>
                        </Grid>
                    </Grid>
                </>
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "dwtMin",
            Title: "Min Dwt",
            AllowSort: true,
            Render: (rowData: CargoModel) => {
                return <>{rowData.dwtMin.toLocaleString()}</>;
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "dwtMax",
            Title: "Max Dwt",
            AllowSort: true,
            Render: (rowData: CargoModel) => {
                return <>{rowData.dwtMax.toLocaleString()}</>;
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "loadPortName",
            Title: "Loadport",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "loadPortArea",
            Title: "Load Area",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "disportName",
            Title: "Disport",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "disportArea",
            Title: "Dis Area",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "chartererCountry",
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "lay",
            Title: "Lay Can",
            AllowSort: true,
            Render: (rowData: CargoModel) => (
                <>
                    {formatDateMMMDD(rowData.lay).toUpperCase() + " - " + formatDateMMMDD(rowData.can).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "updatedDate",
            Title: "Update Time",
            AllowSort: true,
            Render: (rowData: CargoModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate)}
                </>
            )
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "userDisplayName",
            Title: "By",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "accountEmails",
            Title: "Emails",
            AllowSort: false,
            Width: "50px",
            Render: (rowData: CargoModel) => {
                return <>
                    <Button
                        className="table-emails"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.accountEmails && rowData.accountEmails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOrderDetail(rowData.accountEmails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.accountEmails || rowData.accountEmails.length === 0}
                    >
                        <i className="fa fa-envelope-o" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: CargoModel) => (
                <>
                    <Tooltip title="Matching Areas 1">
                        <Button
                            className='table-action-button'
                            onClick={() => {
                                matchingAreas(rowData, MatchingType.MatchingArea1);
                            }}
                            variant='outlined'
                        >M1</Button>
                    </Tooltip>
                    <Tooltip title="Matching Areas 2">
                        <Button
                            className='table-action-button'
                            onClick={() => {
                                matchingAreas(rowData, MatchingType.MatchingArea2);
                            }}
                            variant='outlined'
                        >M2</Button>
                    </Tooltip>
                    <Tooltip title="Marketing Matching">
                        <Button
                            className='table-action-button'
                            onClick={() => {
                                matchingAreas(rowData, MatchingType.MarketingMatching);
                            }}
                            variant='outlined'
                        >M</Button>
                    </Tooltip>
                    {userProfile.userGroup === UserGroupEnum.Administrator &&
                        <Tooltip title="Delete Cargo">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteCargo(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<CargoModel>,
    ];

    const columnsForCargoCopy = [
        {
            Field: "chartererCompanyName",
            Title: "Account Name",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "dwtMin",
            Title: "Min Dwt",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "dwtMax",
            Title: "Max Dwt",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "loadPortName",
            Title: "Loadport",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "disportName",
            Title: "Disport",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "lay",
            Title: "Lay Can",
            AllowSort: true,
            Render: (rowData: CargoModel) => (
                <>
                    {formatDateMMMDD(rowData.lay).toUpperCase() + " - " + formatDateMMMDD(rowData.can).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<CargoModel>,
    ];

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "cargo-datatable-search-result nice-scroll";

        if (!cargoInputViewData.cargoInputExpand) {
            className += " cargo-datatable-search-result-input-collapse";
        }

        return className;
    }

    const deleteCargo = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this cargo? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                CargoApi.DeleteDuplicateCargos({
                    ids: [id]
                } as DeleteDuplicateCargosParam)
                    .then((response: BaseResponse<CargoModel[]>) => {
                        if (response.success) {
                            Utility.Alert.Success("Cargo deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerCargoSearch(new Date().getTime()));
                        }
                    });
            }
        })
    };

    const matchingAreas = (rowData: CargoModel, matchingType: MatchingType) => {
        let lay = "", can = "";
        let areaQuery = [] as QueryItem[];
        let excludedCountries = [] as QueryItem[];
        let excludedCompanies = [] as QueryItem[];
        let marketingMatching = false;

        if (matchingType === MatchingType.MatchingArea1) {
            let matchingArea = Matching.getMatchingAreas(rowData.loadPortArea, rowData.dwtMax);
            if (!matchingArea) {
                Utility.Alert.Error("Can not matching with area: " + rowData.loadPortArea);
                return;
            }

            lay = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.lay), -5));
            can = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.can), 3));

            areaQuery = _.map(matchingArea.first, (matchingArea) => {
                return {
                    id: matchingArea,
                    value: matchingArea,
                    positive: true
                } as QueryItem;
            })
            marketingMatching = false;
        } else if (matchingType === MatchingType.MatchingArea2) {
            let matchingArea = Matching.getMatchingAreas(rowData.loadPortArea, rowData.dwtMax);
            if (!matchingArea) {
                Utility.Alert.Error("Can not matching with area: " + rowData.loadPortArea);
                return;
            }

            lay = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.lay), -5));
            can = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.can), 5));

            areaQuery = _.map(matchingArea.second, (matchingArea) => {
                return {
                    id: matchingArea,
                    value: matchingArea,
                    positive: true
                } as QueryItem;
            })
            marketingMatching = false;
        } else {
            if (rowData.chartererCountry) {
                excludedCountries = [{
                    id: rowData.chartererCountry,
                    value: rowData.chartererCountry,
                    positive: true
                } as QueryItem];
            }

            if (rowData.chartererCompanyName) {
                excludedCompanies = [{
                    id: rowData.chartererCompanyName,
                    value: rowData.chartererCompanyName,
                    positive: true
                } as QueryItem]
            }

            marketingMatching = true;
        }

        let matchingQuery = {
            lay: lay,
            can: can,
            dwtMin: rowData.dwtMin,
            dwtMax: rowData.dwtMax,
            tonnageDwt: 0,
            loadAreas: areaQuery,
            disAreas: [],
            excludedCountries: excludedCountries,
            excludedCompanies: excludedCompanies,
            marketingMatching: marketingMatching,
            orderDirection: OrderDirection.Desc,
            orderBy: "updatedDate"
        } as MatchingQueryParam;

        dispatch(setMatchingQuery(matchingQuery));

        if (marketingMatching) {
            //marketing matching
            dispatch(setComponentType(ComponentType.Cargo));
            dispatch(setOpenModal(true));
            dispatch(setTriggerMarketingMatching((new Date()).getTime()));
        } else {
            //matching
            let selectedMatchingCargo = {
                chartererCompanyName: rowData.chartererCompanyName,
                cargoTypeName: rowData.cargoTypeName,
                dwtMin: rowData.dwtMin,
                dwtMax: rowData.dwtMax,
                loadPortName: rowData.loadPortName,
                disportName: rowData.disportName,
            } as CargoMatchingDisplayModel;

            let json = JSON.stringify(matchingQuery);

            let matchingTypeString = matchingType == MatchingType.MatchingArea1 ? "M1" : "M2";

            let url = "/tonnage?componentType=" + ComponentType.Tonnage + "&matchingQuery=" + encodeURIComponent(json) + "&matchingType=" + matchingTypeString + "&matchingModel=" + encodeURIComponent(JSON.stringify(selectedMatchingCargo));

            const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
            if (newWindow) newWindow.opener = null
        }
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        dispatch(setOpenModal(true));
                    }}
                >Preview Matching</Button>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let emails = "";
                        _.each(cargoData.checkedCargos, (cargo) => {
                            if (cargo.accountEmails && cargo.accountEmails.length > 0) {
                                emails += cargo.accountEmails + ";";
                            }
                        });
                        emails = emails.replace(/\;\;/g,';')
                        navigator.clipboard.writeText(emails);
                        Utility.Alert.Success("Copied to clipboard");
                    }}
                >Copy emails to clipboard</Button>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("tonnage-table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "tonnage-table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "tonnage-table-copy-hide";
                    }}
                >Copy cargo list to clipboard</Button>
            </Grid>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {cargoData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<CargoModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={cargoData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<CargoModel>, orderDirection: OrderDirection) => {
                if (cargoData.searchType === SearchType.Search) {
                    dispatch(setOrderBy(column.Field));
                    dispatch(setOrderDirection(orderDirection));
                    dispatch(setTriggerCargoSearch((new Date()).getTime()));
                } else {
                    dispatch(setMatchingOrderBy(column.Field));
                    dispatch(setMatchingOrderDirection(orderDirection));
                    dispatch(setTriggerMatching((new Date()).getTime()));
                }
            }}
            onRowSelectedChange={(cargo: CargoModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedCargoId(cargo.id))
                    dispatch(setDatatableSelectedCargoIndex(index));
                }
            }}
            selectedRowIndex={cargoData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={cargoData.searchType === SearchType.Search ? cargoData.query.orderBy : matchingSelectorData.matchingQuery.orderBy}
            orderDirection={cargoData.searchType === SearchType.Search ? cargoData.query.orderDirection : matchingSelectorData.matchingQuery.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: CargoModel[]) => {
                dispatch(setCheckedCargos(selectedRows));
            }}
            isDisableRow={(row: CargoModel) => {
                return row.fixed;
            }}
            isDisableRowClassName="cargo-row-fixed"
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{orderDetail}</Typography>
        </Popover>
        <div id="tonnage-table-copy"
            className='tonnage-table-copy-hide'
            contentEditable="true"
            ref={divRef}
        >
            <Datatable<CargoModel>
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={cargoData.checkedCargos}
                useItemPerPage={false}
                columns={columnsForCargoCopy}
                isDataLoaded={true}
                className={getTableClassName()}
            ></Datatable>
        </div>
    </div>
};

export default CargoTable;